import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faCaretDown,
  faCaretRight,
} from "@fortawesome/free-solid-svg-icons";
import cardPaymentModelStyles from "./CardPaymentModel.module.css";
import safeDropdownStyles from "../SafeDropdown/SafeDropdown.module.css";
import CalendarIcon from "../../assets/Calendar_Icon.svg";
import { useTranslation } from "react-i18next";
import { getCardBrandIcon } from "../../utils/cardBrandIcon";
import { useGlacierKey, useAddCustomer } from "../../hooks/useGlacierCard";
import { useApiErrorMessage } from "../../hooks/useApiErrorMessage";
import {
  cardNotAcceptedMessage,
  isCardNotAcceptedError,
  isDisplayableMessage,
} from "../../utils/errorMessages";

const SafeDropdown = ({ onClick }) => {
  const { t } = useTranslation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const handleDropdownClick = () => {
    setDropdownOpen(!isDropdownOpen);
    onClick();
  };

  return (
    <div>
      <button
        className={safeDropdownStyles["safe-dropdown-button"]}
        onClick={handleDropdownClick}
        type="button"
      >
        {isDropdownOpen ? (
          <FontAwesomeIcon
            icon={faCaretDown}
            className={safeDropdownStyles.icon}
          />
        ) : (
          <FontAwesomeIcon
            icon={faCaretRight}
            className={safeDropdownStyles.icon}
          />
        )}
        <div className={safeDropdownStyles.lockIconContainer}>
          <FontAwesomeIcon
            icon={faLock}
            className={safeDropdownStyles.lockIcon}
          />
        </div>
        {t("Why is this safe")}
      </button>
      {isDropdownOpen && (
        <div>{t("WWG has completed the required procedures")}</div>
      )}
    </div>
  );
};

SafeDropdown.propTypes = {
  onClick: PropTypes.func.isRequired,
};

// Standard "MM/YY" card-expiry formatting: strip non-digits, cap at 4
// digits, auto-insert the "/" once the month is complete. Backspacing
// through the auto-inserted "/" falls out naturally since the raw input
// is re-derived from digits only on every keystroke.
function splitExpiry(value) {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 4);
  return {
    expireMonth: digits.slice(0, 2),
    expireYear: digits.slice(2, 4),
  };
}

// The backend never sends more than the last four digits of an existing
// card (firstSix is redacted server-side for PCI compliance), so the best
// we can display is a standard "stars + last four" mask.
function maskCardNumber(lastFour) {
  const digits = String(lastFour ?? "")
    .replace(/\D/g, "")
    .slice(-4);
  return `•••• •••• •••• ${digits || "••••"}`;
}

// Builds the initial "MM/YY" from separate month/year props. Values may
// arrive as "10"/"2030" (4-digit year) or "10"/"30" — normalize each part
// independently rather than concatenating raw strings, which previously
// mis-truncated years like "2030" into "20".
function toExpiryString(month, year) {
  const monthDigits = String(month ?? "")
    .replace(/\D/g, "")
    .padStart(2, "0")
    .slice(-2);
  const yearDigits = String(year ?? "")
    .replace(/\D/g, "")
    .slice(-2)
    .padStart(2, "0");
  if (!month && !year) return "";
  return `${monthDigits}/${yearDigits}`;
}

const NICKNAME_MAX_LENGTH = 20;
const ZIP_MAX_LENGTH = 5;
const LIMIT_TOOLTIP_MS = 2000;
const LIMIT_TOOLTIP_FADE_MS = 300;

// Shared between the metadata-only (edit) and Collect.js (add card) submit
// paths so both flows enforce the exact same ZIP rules.
function getZipIssue(zip) {
  if (!zip) return "zip-required";
  if (zip.length !== ZIP_MAX_LENGTH) return "zip-format";
  return null;
}

// Shows a "you hit the character limit" tooltip that auto-fades after a
// few seconds of no further input, re-arming the timer on every keystroke
// that re-triggers it (rather than only on the false->true transition).
function useLimitTooltip() {
  const [state, setState] = useState({ show: false, visible: false });
  const hideTimerRef = useRef(null);
  const unmountTimerRef = useRef(null);

  const clearTimers = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (unmountTimerRef.current) clearTimeout(unmountTimerRef.current);
  };

  const trigger = () => {
    clearTimers();
    setState({ show: true, visible: true });
    hideTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, visible: false }));
      unmountTimerRef.current = setTimeout(
        () => setState({ show: false, visible: false }),
        LIMIT_TOOLTIP_FADE_MS
      );
    }, LIMIT_TOOLTIP_MS);
  };

  const dismiss = () => {
    clearTimers();
    setState({ show: false, visible: false });
  };

  useEffect(() => clearTimers, []);

  return [state, trigger, dismiss];
}

const CardPaymentModel = ({
  onCancel,
  onUpdatePayment,
  initialZipcode = "",
  initialNickname = "",
  initialExpireMonth = "",
  initialExpireYear = "",
  initialIsPrimary = false,
  initialLastFour = "",
  initialCardType = "",
  metadataOnly = false,
}) => {
  const { t } = useTranslation();
  const apiErrorMessage = useApiErrorMessage();

  const toErrorMessage = (err, fallbackText) => {
    const fallback = fallbackText || t("Update payment failed");
    if (!err) return fallback;

    if (isCardNotAcceptedError(err)) {
      return t(cardNotAcceptedMessage.key, cardNotAcceptedMessage.fallback);
    }

    // Errors from parseApiError carry the developer string on `message`; the
    // shared resolver decides what is safe to show instead.
    if (typeof err === "string" || err?.status) {
      return apiErrorMessage(err, fallback);
    }

    // Everything below is a Glacier SDK rejection rather than one of ours, so
    // it still has to clear the leak filter before it reaches the user.
    const safe = (value) =>
      typeof value === "string" && isDisplayableMessage(value) ? value : null;

    const rawMessage = err?.message;
    const candidates = [
      rawMessage,
      rawMessage?.message,
      rawMessage?.error,
      rawMessage?.message?.message,
      rawMessage?.message?.error,
      err?.response?.data?.message,
      err?.response?.data?.error,
    ];

    for (const candidate of candidates) {
      const usable = safe(candidate);
      if (usable) return usable;
    }

    return fallback;
  };

  const [zipcode, setZipcode] = useState(initialZipcode);
  const [nickname, setNickname] = useState(initialNickname);
  const [expiry, setExpiry] = useState(
    toExpiryString(initialExpireMonth, initialExpireYear)
  );
  const [, setIsPrimary] = useState(Boolean(initialIsPrimary));
  const [error, setError] = useState(null);
  // Tracks which specific validation rule(s) are currently failing, e.g.
  // "zip-required", "zip-format". Kept as issue keys (not pre-translated
  // strings) so the on-screen list stays in sync as the user corrects
  // individual fields.
  const [formIssues, setFormIssues] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [nicknameLimit, triggerNicknameLimit, dismissNicknameLimit] =
    useLimitTooltip();
  const [zipLimit, triggerZipLimit, dismissZipLimit] = useLimitTooltip();

  const zipIssues = formIssues.filter((issue) => issue.startsWith("zip-"));
  const zipInvalid = zipIssues.length > 0;

  const ISSUE_MESSAGES = {
    "zip-required": "ZIP code is required",
    "zip-format": "ZIP code must be 5 digits",
  };

  // Set right before startPayment() so a Collect.js token that fires without
  // our own submit click having run (e.g. some other auto-trigger) is ignored
  // instead of silently completing a purchase the user never confirmed.
  const paymentArmedRef = useRef(false);

  // Ref so the onToken closure always reads the latest form values
  const formRef = useRef({
    zipcode: initialZipcode,
    nickname: initialNickname,
    expiry: toExpiryString(initialExpireMonth, initialExpireYear),
    isPrimary: Boolean(initialIsPrimary),
  });

  const addCustomer = useAddCustomer();

  const { load, isReady, fieldErrors, startPayment } = useGlacierKey({
    onToken: async (paymentToken) => {
      if (!paymentArmedRef.current) return;
      paymentArmedRef.current = false;
      try {
        const customerData = await addCustomer.mutateAsync({
          paymentToken,
          postalCode: formRef.current.zipcode,
          nickname: formRef.current.nickname,
          isPrimary: formRef.current.isPrimary,
        });
        await Promise.resolve(
          onUpdatePayment?.({
            ...customerData,
            postalCode: formRef.current.zipcode,
            nickname: formRef.current.nickname,
            isPrimary: formRef.current.isPrimary,
          })
        );
        resetState();
      } catch (err) {
        setError(toErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // If Collect.js flags a field invalid on submit, it fires validationCallback
  // instead of the token callback — so onToken (and its isSubmitting reset)
  // never runs. Without this, the button gets stuck on "Processing..." forever.
  useEffect(() => {
    if (
      !metadataOnly &&
      isSubmitting &&
      (fieldErrors.ccnumber || fieldErrors.ccexp)
    ) {
      paymentArmedRef.current = false;
      setIsSubmitting(false);
    }
  }, [fieldErrors, isSubmitting, metadataOnly]);

  // Load Collect.js when modal mounts; clean up on unmount. Not needed in
  // metadataOnly mode since it only edits fields already on file (no new
  // card number/tokenization involved).
  useEffect(() => {
    if (metadataOnly) return undefined;
    let cancelled = false;
    setError(null);
    load.mutateAsync().catch((err) => {
      if (!cancelled) setError(toErrorMessage(err));
    });
    return () => {
      cancelled = true;
      window.CollectJS = null;
    };
  }, [metadataOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const nextExpiry = toExpiryString(initialExpireMonth, initialExpireYear);
    setZipcode(initialZipcode);
    setNickname(initialNickname);
    setExpiry(nextExpiry);
    setIsPrimary(Boolean(initialIsPrimary));
    formRef.current = {
      zipcode: initialZipcode,
      nickname: initialNickname,
      expiry: nextExpiry,
      isPrimary: Boolean(initialIsPrimary),
    };
  }, [
    initialZipcode,
    initialNickname,
    initialExpireMonth,
    initialExpireYear,
    initialIsPrimary,
  ]);

  const handleZipcodeChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    const val = raw.slice(0, ZIP_MAX_LENGTH);
    setZipcode(val);
    formRef.current.zipcode = val;
    if (!getZipIssue(val))
      setFormIssues((prev) =>
        prev.filter((issue) => !issue.startsWith("zip-"))
      );
    else if (val)
      setFormIssues((prev) => prev.filter((issue) => issue !== "zip-required"));
    if (raw.length > ZIP_MAX_LENGTH) triggerZipLimit();
    else dismissZipLimit();
  };

  const handleNicknameChange = (e) => {
    const raw = e.target.value;
    const val = raw.slice(0, NICKNAME_MAX_LENGTH);
    setNickname(val);
    formRef.current.nickname = val;
    if (raw.length > NICKNAME_MAX_LENGTH) triggerNicknameLimit();
    else dismissNicknameLimit();
  };

  const resetState = () => {
    setZipcode("");
    setNickname("");
    setExpiry("");
    setIsPrimary(false);
    formRef.current = {
      zipcode: "",
      nickname: "",
      expiry: "",
      isPrimary: false,
    };
    setError(null);
    setFormIssues([]);
    setIsSubmitting(false);
    setHasAttemptedSubmit(false);
    paymentArmedRef.current = false;
    dismissNicknameLimit();
    dismissZipLimit();
  };

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);

    if (metadataOnly) {
      const zipIssue = getZipIssue(zipcode);
      const issues = [zipIssue].filter(Boolean);
      if (issues.length > 0) {
        setFormIssues(issues);
        setError(null);
        return;
      }
      setFormIssues([]);
      setError(null);

      const hasChanges =
        formRef.current.zipcode !== initialZipcode ||
        formRef.current.nickname !== initialNickname;

      if (!hasChanges) {
        onCancel();
        resetState();
        return;
      }

      setIsSubmitting(true);

      // Expiry is no longer user-editable here, but the update-card schema
      // still requires it, so pass the original value through unchanged.
      const { expireMonth, expireYear } = splitExpiry(
        toExpiryString(initialExpireMonth, initialExpireYear)
      );
      Promise.resolve(
        onUpdatePayment?.({
          postalCode: formRef.current.zipcode,
          nickname: formRef.current.nickname,
          expireMonth,
          expireYear,
          isPrimary: formRef.current.isPrimary,
        })
      )
        .catch((err) => {
          setError(toErrorMessage(err, t("Update payment failed")));
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    const zipIssue = getZipIssue(zipcode);
    if (zipIssue) {
      setFormIssues([zipIssue]);
      setError(null);
      return;
    }
    setFormIssues([]);
    setError(null);
    setIsSubmitting(true);

    // CollectJS validates iframes then fires onToken callback above
    paymentArmedRef.current = true;
    startPayment();
  };

  return (
    <div className={cardPaymentModelStyles["payment-container"]}>
      <div className={cardPaymentModelStyles.section}>
        <div className={cardPaymentModelStyles.header}>
          <h3>{t("Payment information")}</h3>
          <div className={cardPaymentModelStyles.acceptedCards}>
            {t("We currently accept")}
          </div>
        </div>

        <div
          className={`${cardPaymentModelStyles.section} ${
            cardPaymentModelStyles["zipcode"]
          } ${zipInvalid ? cardPaymentModelStyles.invalid : ""}`}
        >
          <div className={cardPaymentModelStyles.fieldRow}>
            <label htmlFor="zipCode">{t("Zip Code:")}</label>
            <input
              type="text"
              id="zipCode"
              name="zipCode"
              value={zipcode}
              onChange={handleZipcodeChange}
              placeholder={t("Enter ZIP Code")}
              className={cardPaymentModelStyles.inputField}
            />
          </div>
          {zipIssues.length > 0 && (
            <span className={cardPaymentModelStyles.fieldTopError}>
              {t(ISSUE_MESSAGES[zipIssues[0]])}
            </span>
          )}
          {zipLimit.show && (
            <span
              className={`${cardPaymentModelStyles.limitTooltip} ${
                zipLimit.visible
                  ? ""
                  : cardPaymentModelStyles.limitTooltipHidden
              }`}
            >
              {t("ZIP code must be 5 digits")}
            </span>
          )}
        </div>

        <div
          className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["nickname"]}`}
        >
          <div className={cardPaymentModelStyles.fieldRow}>
            <label htmlFor="nickname">{t("Nickname")}</label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={nickname}
              onChange={handleNicknameChange}
              placeholder={t("Enter Nickname")}
              className={cardPaymentModelStyles.inputField}
            />
          </div>
          {nicknameLimit.show && (
            <span
              className={`${cardPaymentModelStyles.limitTooltip} ${
                nicknameLimit.visible
                  ? ""
                  : cardPaymentModelStyles.limitTooltipHidden
              }`}
            >
              {t("Nickname must be less than 20 characters")}
            </span>
          )}
        </div>

        {metadataOnly ? (
          <>
            <div
              className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["cardnumber"]} ${cardPaymentModelStyles.disabledSection}`}
            >
              <div className={cardPaymentModelStyles.fieldRow}>
                <label htmlFor="cardNumber">
                  <FontAwesomeIcon
                    icon={getCardBrandIcon(initialCardType)}
                    title={initialCardType || t("Card Number")}
                    className={cardPaymentModelStyles.cardBrandLabelIcon}
                  />
                </label>
                <input
                  type="text"
                  id="cardNumber"
                  name="cardNumber"
                  value={maskCardNumber(initialLastFour)}
                  disabled
                  readOnly
                  className={cardPaymentModelStyles.inputField}
                />
              </div>
            </div>

            <div
              className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["expiry"]} ${cardPaymentModelStyles.disabledSection}`}
            >
              <div className={cardPaymentModelStyles.fieldRow}>
                <label htmlFor="expiry">
                  <img
                    src={CalendarIcon}
                    alt={t("Expiration")}
                    className={cardPaymentModelStyles.labelIcon}
                  />
                </label>
                <input
                  type="text"
                  id="expiry"
                  name="expiry"
                  inputMode="numeric"
                  value={expiry}
                  placeholder={t("MM/YY")}
                  maxLength={5}
                  disabled
                  readOnly
                  className={cardPaymentModelStyles.inputField}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Collect.js injects iframes into these divs */}
            <div
              className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["cardnumber"]}`}
            >
              <div className={cardPaymentModelStyles.fieldRow}>
                <label>
                  <FontAwesomeIcon
                    icon={getCardBrandIcon()}
                    title={t("Card Number")}
                    className={cardPaymentModelStyles.cardBrandLabelIcon}
                  />
                </label>
                <div
                  id="ccnumber"
                  className={cardPaymentModelStyles.collectField}
                />
              </div>
              {hasAttemptedSubmit && fieldErrors.ccnumber && (
                <span className={cardPaymentModelStyles.fieldError}>
                  {t("Enter a valid card number")}
                </span>
              )}
            </div>

            <div
              className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["expiration-container"]}`}
            >
              <div className={cardPaymentModelStyles.fieldRow}>
                <label>
                  <img
                    src={CalendarIcon}
                    alt={t("Expiration")}
                    className={cardPaymentModelStyles.labelIcon}
                  />
                </label>
                <div
                  id="ccexp"
                  className={cardPaymentModelStyles.collectField}
                />
              </div>
              {hasAttemptedSubmit && fieldErrors.ccexp && (
                <span className={cardPaymentModelStyles.fieldError}>
                  {t("Enter a valid expiration date")}
                </span>
              )}
            </div>
          </>
        )}

        {error && (
          <div className={cardPaymentModelStyles.errorMessage}>{error}</div>
        )}

        <div
          className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["dropdown"]}`}
        >
          <SafeDropdown onClick={() => {}} />
        </div>

        <div
          className={`${cardPaymentModelStyles.section} ${cardPaymentModelStyles["button-container"]}`}
        >
          <button
            onClick={() => {
              onCancel();
              resetState();
            }}
            className={cardPaymentModelStyles.cancelButton}
            type="button"
          >
            {t("Cancel/Close")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={metadataOnly ? isSubmitting : !isReady || isSubmitting}
            className={cardPaymentModelStyles.updateButton}
            type="button"
          >
            {isSubmitting
              ? t("Processing...")
              : metadataOnly
              ? t("Update Payment")
              : t("Add Card")}
          </button>
        </div>
      </div>
    </div>
  );
};

CardPaymentModel.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onUpdatePayment: PropTypes.func.isRequired,
  initialZipcode: PropTypes.string,
  initialNickname: PropTypes.string,
  initialExpireMonth: PropTypes.string,
  initialExpireYear: PropTypes.string,
  initialIsPrimary: PropTypes.bool,
  initialLastFour: PropTypes.string,
  initialCardType: PropTypes.string,
  metadataOnly: PropTypes.bool,
};

export default CardPaymentModel;
